// Aspirants on the blåtur page: propose new people, follow their progress towards meeting
// REQUIRED_MET people from the invitation list, veto, and (organizers) admit them.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { app } from './firebase-config';
import { normalizeName, loadDirectory, useRoles } from './adminData';
import { REQUIRED_MET, aspirantStatus } from './aspirants';
import { loadAspirants, nominateAspirant, setVeto, withdrawAspirant, admitAspirant } from './blaturMembers';
import { SmartImage } from './imageUtils';

const db = getFirestore(app);
const initials = (name) => name.trim().split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase();
const date = (iso) => (iso ? new Date(iso).toLocaleDateString('no-NO', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

function BlaturAspiranter({ user, myName, memberRows, profileImages = {}, onAdmitted }) {
  const roles = useRoles(user);
  const [aspirants, setAspirants] = useState(null);
  const [events, setEvents] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const activeNames = useMemo(() => memberRows.filter(r => !r.out).map(r => r.name.trim()), [memberRows]);
  const allMemberKeys = useMemo(() => new Set(memberRows.map(r => normalizeName(r.name))), [memberRows]);
  const me = memberRows.find(r => normalizeName(r.name) === normalizeName(myName));
  const canPropose = !!me && !me.out;

  const load = useCallback(async () => {
    const [list, eventsSnap, directory] = await Promise.all([
      loadAspirants(),
      getDocs(collection(db, 'events')),
      loadDirectory().catch(() => ({ names: [] })),
    ]);
    const allEvents = eventsSnap.docs.map(d => d.data());
    setAspirants(list);
    setEvents(allEvents);
    // Suggestions: everyone the app knows, plus names that only appear on events
    const names = new Map(directory.names.map(n => [normalizeName(n), n]));
    allEvents.forEach(e => [...(e.Deltagelse || []), ...(e.Vert || [])].forEach(n => {
      if (n.trim() && !names.has(normalizeName(n))) names.set(normalizeName(n), n.trim());
    }));
    setSuggestions([...names.values()].sort((a, b) => a.localeCompare(b, 'no')));
  }, []);

  useEffect(() => {
    load().catch(err => {
      console.error('Error loading aspirants: ', err);
      setError(err);
    });
  }, [load]);

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
      await load();
    } catch (err) {
      alert('Noe gikk galt: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  if (error) return null;
  if (!aspirants) return null;

  const active = aspirants.filter(a => a.status === 'aspirant');
  const admitted = aspirants.filter(a => a.status === 'admitted').slice(-6).reverse();
  const taken = new Set([...allMemberKeys, ...active.map(a => normalizeName(a.name))]);
  const options = suggestions.filter(n => !taken.has(normalizeName(n)));

  const propose = (e) => {
    e.preventDefault();
    const clean = name.trim();
    if (!clean) return;
    if (taken.has(normalizeName(clean))) return alert(`${clean} er allerede på listen eller meldt inn.`);
    run(async () => {
      await nominateAspirant(clean, user, myName);
      setName('');
    });
  };

  const admit = (aspirant, status) => {
    const warnings = [];
    if (!status.ready) warnings.push(`${aspirant.name} har bare møtt ${status.met.length} av ${REQUIRED_MET}.`);
    if (status.blocked) warnings.push(`Veto fra ${status.vetoes.map(v => v.name).join(', ')} blir overstyrt.`);
    const question = `Ta ${aspirant.name} inn på invitasjonslisten?${warnings.length ? '\n\n' + warnings.join('\n') : ''}`;
    if (!window.confirm(question)) return;
    run(async () => {
      await admitAspirant(aspirant, { overrideVeto: status.blocked }, user.uid);
      onAdmitted?.();
    });
  };

  return (
    <section className="blatur-section aspiranter">
      <h2>Aspiranter</h2>
      <p className="blatur-muted">
        Nye kan meldes inn av dem på invitasjonslisten. En aspirant kan inviteres når de har møtt minst {REQUIRED_MET} av
        dem på invitasjonslisten på andre arrangementer. Alle på listen kan legge ned veto, og en veto stopper opptak
        til den trekkes eller arrangørene overstyrer den.
      </p>

      {active.length === 0 && <p className="blatur-muted">Ingen aspiranter akkurat nå.</p>}

      <div className="aspirant-list">
        {active.map(a => {
          const status = aspirantStatus(a, events, activeNames);
          const myVeto = !!a.vetoes?.[user.uid];
          const progress = Math.min(1, status.met.length / REQUIRED_MET);
          const state = status.blocked ? 'blocked' : status.ready ? 'ready' : 'pending';
          return (
            <article key={a.id} className={`aspirant-card ${state}`}>
              <div className="aspirant-head">
                <span className="blatur-avatar aspirant-avatar">
                  {profileImages[normalizeName(a.name)]
                    ? <SmartImage src={profileImages[normalizeName(a.name)]} size={200} />
                    : <span>{initials(a.name)}</span>}
                </span>
                <div className="aspirant-who">
                  <strong>{a.name}</strong>
                  <span className="blatur-muted">Meldt inn av {a.nominatedByName} · {date(a.nominatedAt)}</span>
                </div>
                <span className={`aspirant-chip ${state}`}>
                  {status.blocked ? 'Veto' : status.ready ? 'Klar for opptak' : 'Aspirant'}
                </span>
              </div>

              <div className="aspirant-progress" title={status.met.join(', ')}>
                <div className="aspirant-bar"><span style={{ width: `${progress * 100}%` }} /></div>
                <span className="aspirant-count">Har møtt {status.met.length} av {REQUIRED_MET}</span>
              </div>
              {status.met.length > 0 && <p className="aspirant-met">{status.met.join(', ')}</p>}

              {status.vetoes.length > 0 && (
                <p className="aspirant-vetoes">Veto fra {status.vetoes.map(v => v.name).join(', ')}</p>
              )}

              <div className="aspirant-actions">
                {canPropose && normalizeName(a.name) !== normalizeName(myName) && (
                  <button
                    type="button"
                    className={`button-17 muted${myVeto ? ' active-veto' : ''}`}
                    disabled={busy}
                    onClick={() => {
                      if (!myVeto && !window.confirm(`Legge ned veto mot ${a.name}? Vetoen blir synlig for alle.`)) return;
                      run(() => setVeto(a, user, myName, !myVeto));
                    }}
                  >
                    {myVeto ? 'Trekk vetoen min' : 'Legg ned veto'}
                  </button>
                )}
                {roles.isBlaturAdmin && (
                  <button type="button" className="button-17" disabled={busy} onClick={() => admit(a, status)}>
                    {status.blocked ? 'Overstyr veto og ta inn' : 'Ta inn på invitasjonslisten'}
                  </button>
                )}
                {(roles.isBlaturAdmin || a.nominatedBy === user.uid) && (
                  <button
                    type="button"
                    className="button-17 muted"
                    disabled={busy}
                    onClick={() => window.confirm(`Trekke innmeldingen av ${a.name}?`) && run(() => withdrawAspirant(a))}
                  >
                    Trekk innmelding
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {canPropose && (
        <form className="aspirant-form" onSubmit={propose}>
          <input
            list="aspirant-names"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Navn på den du vil melde inn"
          />
          <datalist id="aspirant-names">
            {options.map(n => <option key={n} value={n} />)}
          </datalist>
          <button type="submit" className="button-17" disabled={busy || !name.trim()}>Meld inn</button>
        </form>
      )}
      {canPropose && (
        <p className="blatur-muted aspirant-hint">
          Bruk samme navn som personen er registrert med på eventene, så telles møtene riktig.
        </p>
      )}

      {admitted.length > 0 && (
        <div className="aspirant-history">
          <h3>Tatt inn</h3>
          <ul>
            {admitted.map(a => {
              const vetoNames = Object.values(a.vetoes || {}).map(v => v.name);
              return (
                <li key={a.id}>
                  <strong>{a.name}</strong> · meldt inn av {a.nominatedByName}, tatt inn {date(a.admittedAt)}
                  {vetoNames.length > 0 && <> · <span className="aspirant-vetoes">veto fra {vetoNames.join(', ')}{a.overrideVeto ? ' (overstyrt)' : ''}</span></>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}

export default BlaturAspiranter;
